import "./boxModel.css";
import { useState, useEffect, useRef } from "react";
import Overlay from "../ProductRightSlider/ProductRightSlider";
import { getPriceLightBgColor } from "../../utils/HelperFunctions";
import { globalGradientDarkColors } from "../../utils/GlobalConstants";
import { ReactComponent as GiftDark } from "../../assets/icons/boxes.svg";
import { ReactComponent as GiftLight } from "../../assets/icons/boxes.svg";
import {
  getPriceDarkHoverColor,
  getPriceDarkBorderColor,
} from "../../utils/GlobalConstants";

const BoxModel = ({ show, onHide, item, isDarkMode, onOpenBox }) => {
  const [isAnimating, setIsAnimating] = useState(false);
  const [showOverlay, setShowOverlay] = useState(false);
  const [overlayData, setOverlayData] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 641);
  const [isAtBottom, setIsAtBottom] = useState(false);
  const [hoveredId, setHoveredId] = useState(null);

  const [dragX, setDragX] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const startX = useRef(0);
  const modalRef = useRef(null);
  const bodyRef = useRef(null);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 641);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (show) {
      setDragX(0);
      setIsDragging(false);
      requestAnimationFrame(() => {
        setIsAnimating(true);
      });
    } else {
      setIsAnimating(false);
    }
  }, [show]);

  // Handle scroll to detect if at bottom
  const handleScroll = () => {
    if (!bodyRef.current || !isMobile) return;

    const { scrollTop, scrollHeight, clientHeight } = bodyRef.current;
    const threshold = 10; // 10px threshold
    const atBottom = scrollHeight - scrollTop - clientHeight <= threshold;

    setIsAtBottom(atBottom);
  };

  useEffect(() => {
    const bodyElement = bodyRef.current;
    if (bodyElement && isMobile) {
      bodyElement.addEventListener("scroll", handleScroll);
      // Check initial scroll position
      handleScroll();

      return () => {
        bodyElement.removeEventListener("scroll", handleScroll);
      };
    }
  }, [isMobile, show]);

  // Swipe handlers for mobile - DOWN TO CLOSE
  const handleTouchStart = (e) => {
    if (!isMobile) return;
    startX.current = e.touches[0].clientY;
    setIsDragging(true);
  };

  const handleTouchMove = (e) => {
    if (!isMobile || !isDragging) return;
    const currentY = e.touches[0].clientY;
    const diff = currentY - startX.current;

    if (diff > 0) {
      setDragX(diff);
    }
  };

  const handleTouchEnd = () => {
    if (!isMobile) return;
    setIsDragging(false);

    if (dragX > 150) {
      onHide();
    }
    setDragX(0);
  };

  if (!show && !isAnimating) return null;

  const getPriceDarkBgColor = (price) => {
    if (price <= 20) return globalGradientDarkColors.commanGray;
    if (price <= 50) return globalGradientDarkColors.greenUncomman;
    if (price <= 250) return globalGradientDarkColors.blueRare;
    if (price <= 2000) return globalGradientDarkColors.purpleEpic;
    return globalGradientDarkColors.goldLegendary;
  };

  const handleProductClick = (product) => {
    setOverlayData(product);
    setShowOverlay(true);
  };

  const backdropOpacity = isMobile ? Math.max(0, 0.7 - dragX / 400) : 0.7;

  return (
    <>
      <Overlay
        showOverlay={showOverlay}
        setShowOverlay={setShowOverlay}
        overlayData={overlayData}
      />

      <div
        className={`box-modal-overlay ${isMobile ? "mobile-swipe-modal" : ""} ${
          isAnimating ? "active" : ""
        } ${show ? "visible" : ""} ${isDarkMode ? "dark" : "light"}`}
        style={{
          backgroundColor: isAnimating
            ? `rgba(0, 0, 0, ${backdropOpacity})`
            : "rgba(0, 0, 0, 0)",
          transition: isDragging ? "none" : "background-color 0.6s ease",
          opacity: show ? 1 : 0,
        }}
        onClick={(e) => {
          e.stopPropagation();
          onHide();
        }}
      >
        <div
          ref={modalRef}
          className={`box-modal-content ${isMobile ? "mobile" : "desktop"} ${
            isAnimating ? "animating" : ""
          }`}
          style={{
            transform: isMobile
              ? isAnimating
                ? `translateY(${dragX}px)`
                : "translateY(100%)"
              : isAnimating
                ? "translateX(0)"
                : "translateX(100%)",
            transition: isDragging
              ? "none"
              : isMobile
                ? "transform 0.6s cubic-bezier(0.4, 0, 0.2, 1)"
                : "transform 0.9s cubic-bezier(0.4, 0, 0.2, 1)",
          }}
          onClick={(e) => e.stopPropagation()}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          {isMobile && (
            <div
              className={`swipe-handle-container ${isDarkMode ? "swipe-handle-container-bx" : "swipe-handle-container-bx-light"}`}
            >
              <div className="swipe-handle" />
            </div>
          )}

          <div className={`box-modal-header ${isMobile ? "mobile" : ""}`}>
            <div
              className={`box-modal-title-section ${isMobile ? "mobile" : ""}`}
            >
              {isDarkMode ? (
                <GiftDark size={isMobile ? 24 : 32} className="box-gift-icon" />
              ) : (
                <GiftLight
                  size={isMobile ? 24 : 32}
                  className="box-gift-icon"
                />
              )}

              <div className="box-modal-title-wrapper">
                <h2 className={`box-modal-title ${isMobile ? "mobile" : ""}`}>
                  {item?.name || "N/A"}
                </h2>
              </div>
            </div>

            <button
              className={`box-open-button ${isMobile ? "mobile" : ""}`}
              onClick={(e) => {
                e.stopPropagation();
                onHide();
              }}
            >
              <i
                className="fa-solid fa-close"
                style={{ color: "#fff", fontSize: "18px" }}
              ></i>
            </button>
          </div>

          <div
            ref={bodyRef}
            className={`box-modal-body ${isMobile ? "mobile" : ""}`}
          >
            <div className={`box-products-grid ${isMobile ? "mobile" : ""}`}>
              {item?.products && item.products.length > 0 ? (
                item.products.map((product) => (
                  <div
                    key={product.id}
                    onClick={() => handleProductClick(product)}
                    className={`box-product-card ${isMobile ? "mobile" : ""}`}
                    style={{
                      background:
                        hoveredId === product.id
                          ? getPriceDarkHoverColor(product.price)
                          : isMobile
                            ? getPriceDarkHoverColor(product.price)
                            : getPriceDarkBgColor(product.price),
                      borderTop:
                        hoveredId === product.id
                          ? `1px solid ${getPriceDarkBorderColor(product.price)}`
                          : isMobile
                            ? `1px solid ${getPriceDarkBorderColor(product.price)}`
                            : "1px solid transparent",
                      transition: "background 0.2s ease, border-top 0.2s ease",
                    }}
                    onMouseEnter={() => setHoveredId(product.id)}
                    onMouseLeave={() => setHoveredId(null)}
                  >
                    <div className="box-product-image-wrapper">
                      <img
                        src={product.image}
                        alt={product.name}
                        className={`box-product-image ${
                          isMobile ? "mobile" : ""
                        }`}
                        onError={(e) => {
                          e.target.src =
                            "https://via.placeholder.com/300x200?text=No+Image";
                        }}
                      />
                      <div
                        className={`box-product-percentage text-AktivGrotesk-Regular ${
                          isMobile ? "mobile" : ""
                        }`}
                      >
                        {product.percentage}%
                      </div>
                    </div>

                    <div
                      className={`box-product-info ${isMobile ? "mobile" : ""}`}
                    >
                      <h6
                        className={`box-product-name text-center ${
                          isMobile ? "mobile" : ""
                        } ${isDarkMode ? "dark" : ""}`}
                      >
                        {product.name}
                      </h6>

                      <div className="box-product-price-wrapper d-center mt-0">
                        <span
                          className={`box-product-price text-AktivGrotesk-Bold ${
                            isMobile ? "mobile" : ""
                          }`}
                        >
                          {product.formatted_price}
                        </span>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className={`box-no-products ${isDarkMode ? "dark" : ""}`}>
                  <p>No products available in this box</p>
                </div>
              )}
            </div>
            <div className="box-modal-footer">
              <button
                className="box-modal-button "
                onClick={(e) => {
                  e.stopPropagation();
                  onOpenBox();
                }}
              >
                Open Box{" "}
                <span>
                  <svg
                    style={{ marginBottom: "3px" }}
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="black"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <polyline points="6 4 14 12 6 20" />
                    <polyline points="12 4 20 12 12 20" />
                  </svg>
                </span>
              </button>
            </div>
          </div>

          {isMobile && (
            <div className="swipe-handle-container mt-0 swipe-handle-container-bottom">
              <div className="swipe-handle-bottom" />
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default BoxModel;
